import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import format from "date-fns/format";
import { ComponentType, useMemo } from "react";
import { useLocation, Link } from "react-router-dom";
import { CellProps } from "react-table";

import { LabOrder } from "pwapi";

import { getStainByLabStainId } from "shared/utils/stainUtils";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import { useAppSelector } from "store/hooks";

const OrdersColumns =
  <P extends object>(
    Component: ComponentType<P>,
    options?: {
      hasFlagReason?: boolean;
      hasActions?: boolean;
    }
  ) =>
  (props: any) => {
    const location = useLocation();
    const generalSort = (a: string, b: string) =>
      a.localeCompare(b, "en", {
        numeric: true,
        sensitivity: "base",
      });
    const labStains = useAppSelector(({ labStains }) => labStains);
    const stains = useAppSelector(({ stains }) => stains);

    const columns = useMemo(
      () =>
        [
          ...(options?.hasActions
            ? [
                {
                  Header: "Case Accession",
                  accessor: "case__accession",
                },
              ]
            : []),
          {
            Header: "Type",
            accessor: "type",
          },
          {
            Header: "Stain",
            accessor: "stain_name",
            Cell: ({ row }: CellProps<LabOrder>) => (
              <>
                {
                  getStainByLabStainId(
                    row.original.lab_stain_id || -1,
                    stains,
                    labStains
                  )?.name
                }
              </>
            ),
          },
          {
            Header: "Stain Category",
            accessor: "stain_category",
            disableSortBy: true,
            sortType: (a: CellProps<LabOrder>, b: CellProps<LabOrder>) =>
              generalSort(
                a.original.stain?.category || "",
                b.original.stain?.category || ""
              ),
            Cell: ({ row }: CellProps<LabOrder>) => (
              <>
                {
                  getStainByLabStainId(
                    row.original.lab_stain_id || -1,
                    stains,
                    labStains
                  )?.category
                }
              </>
            ),
          },
          {
            Header: "Created At",
            accessor: "created_at",
            Cell: ({ row }: CellProps<LabOrder>) => (
              <>
                {format(new Date(row.original.created_at || ""), "MM/dd/yyyy")}
              </>
            ),
          },
          {
            Header: "Completed At",
            accessor: "completed_at",
            Cell: ({ row }: CellProps<LabOrder>) =>
              row.original.completed_at ? (
                <>{format(new Date(row.original.completed_at), "MM/dd/yyyy")}</>
              ) : (
                <></>
              ),
          },
          ...(options?.hasFlagReason
            ? [
                {
                  Header: "Flag Reason",
                  accessor: "flag_reason",
                  Cell: ({ row }: CellProps<LabOrder>) => (
                    <>
                      {/* This has been broken for a while. does it need to be fixed? */}
                      {/* {row.original.flag &&
                        splitFlagReason(row.original.flag.reason)} */}
                    </>
                  ),
                },
              ]
            : []),
          {
            Header: "Status",
            accessor: "status",
            disableSortBy: true,
            Cell: ({ row }: CellProps<LabOrder>) => (
              // TODO: Orders do not currently have a status, so there's nothing to change
              // <OrderStatusForm status={row.original.status} />
              <>{row.original.cancelled_at ? "Cancelled" : ""}</>
            ),
          },
          {
            Header: "Actions",
            disableSortBy: true,
            Cell: ({ row }: CellProps<LabOrder>) => (
              <div
                className="cell__content--horizontal"
                style={{ gap: "0.5em" }}
              >
                {options?.hasActions && (
                  <Link
                    to={`/admin/cases/${row.original.case_id}`}
                    state={{
                      label: "Orders",
                      prevUrl: `${location.pathname}${location.search}`,
                    }}
                  >
                    <PathologyWatchButton size="small">
                      Edit Case
                    </PathologyWatchButton>
                  </Link>
                )}
                {row.original.slide_id && (
                  <Link
                    to={`/cases/${row.original.case_id}/slides/${row.original.slide_id}`}
                    title="View Slide"
                    target="_blank"
                  >
                    <PathologyWatchButton size="small">
                      <>
                        <FontAwesomeIcon
                          icon="flag"
                          color="var(--imperialRed)"
                        />
                        &nbsp;&rarr;
                      </>
                    </PathologyWatchButton>
                  </Link>
                )}
              </div>
            ),
          },
        ] as any,
      []
    );

    return <Component columns={columns} {...props} />;
  };

export default OrdersColumns;
